// audioRemixTypes.ts — Shared types for the Audio Remix modal

export interface TtsVersion {
  label: string
  path: string
}

export interface TtsSegment {
  character: string
  voice_role_id?: string
  path?: string
  original_path?: string
  start_s: number
  end_s: number
  tts_end_s?: number
  emotion?: string
  status?: string
  type?: string
  text: string
  versions?: TtsVersion[]
}

export interface AudioBlock {
  id: string
  label: string
  audioUrl: string | null
  startS: number
  endS: number
  highlighted: boolean
  fadeInS: number
  fadeOutS: number
  sourceSegmentIdx?: number
  sourceVersionLabel?: string
  sourceDurationS?: number
  sourceType?: 'tts' | 'clip'
}

export interface AudioSegmentGroup {
  id: string
  character: string
  voice_role_id?: string
  emotion?: string
  text: string
  status?: string
  type?: 'dialogue' | 'voiceover'
  tracks: AudioBlock[]
}

export interface AudioRemixConfig {
  groups: Array<{
    id: string
    character: string
    voice_role_id?: string
    emotion?: string
    text?: string
    status?: string
    type?: 'dialogue' | 'voiceover'
    tracks: Array<{
      id: string
      label: string
      startS: number
      endS: number
      highlighted: boolean
      fadeInS?: number
      fadeOutS?: number
      sourceSegmentIdx?: number
      sourceVersionLabel?: string
      sourceDurationS?: number
      sourceType?: 'tts' | 'clip'
    }>
  }>
  sourceOriginalIndex?: number
  sourceOriginalPath?: string
  duckDepthDb?: number | null
}

export type EdgeDragMode = 'clip' | 'fade' | 'speed'

export interface AudioRemixSourceOption {
  index: number
  label: string
  localPath: string
  url: string | null
}

let _blockId = 0
export function nextBlockId() { return `blk_${++_blockId}` }

export function sliceWaveformBars(
  origBars: number[],
  videoDuration: number,
  startS: number,
  endS: number,
  targetBarCount: number,
): number[] {
  if (origBars.length === 0 || videoDuration <= 0) return []
  const startFrac = Math.max(0, startS / videoDuration)
  const endFrac = Math.min(1, endS / videoDuration)
  const startBar = Math.floor(startFrac * origBars.length)
  const endBar = Math.ceil(endFrac * origBars.length)
  const sliced = origBars.slice(startBar, endBar)
  if (sliced.length === 0) return []
  const result: number[] = []
  for (let i = 0; i < targetBarCount; i++) {
    const srcIdx = (i / targetBarCount) * sliced.length
    const lo = Math.floor(srcIdx)
    const hi = Math.min(lo + 1, sliced.length - 1)
    const frac = srcIdx - lo
    result.push(sliced[lo] * (1 - frac) + sliced[hi] * frac)
  }
  return result
}

export interface MixerRowData {
  kind: 'orig' | 'group'
  group: AudioSegmentGroup
  activeTrack: AudioBlock
  versions: AudioBlock[]
  characterColor: CharacterColorTokens
}

export interface CharacterColorTokens {
  fg: string
  bg: string
  border: string
  wave: string
}
